import * as E from '../errors';
import {
	Event,
	EventPhase,
	IEventDescriptor
}             from './Event';
import {
	IListenerDescriptor,
	Listener,
	TListener,
	TListenerCallback
}             from './Listener';

export interface ITargetDescriptor {
};

export abstract class Target<
	TTarget extends TBaseTarget,
	TBaseTarget extends Target<TBaseTarget, TBaseTarget>
> {
	static eventsTypes: {
		type: string;
		subType: string | null;
	}[];

	static eventsDebugger: {
		readonly onEventTrigger: (ev: Event<any, any>) => void;
		readonly onEventTriggered: (ev: Event<any, any>) => void;
		readonly initialDispatcher: typeof Target.prototype.dispatchEvent;
		readonly initialListener: typeof Target.prototype.addEventListener;
	} | null = null;

	static enableDebugAndStrictMode(
		onEventTrigger: (ev: Event<any, any>) => void,
		onEventTriggered: (ev: Event<any, any>) => void
	) {
		if (this.eventsDebugger) {
			return;
		}
		this.eventsDebugger = {
			onEventTrigger,
			onEventTriggered,
			initialDispatcher: Target.prototype.dispatchEvent,
			initialListener: Target.prototype.addEventListener
		};
		Target.prototype.dispatchEvent = (
			Target as any
		).prototype.dispatchEventDebugAndStrictMode;
		Target.prototype.addEventListener = (
			Target as any
		).prototype.addEventListenerDebugAndStrictMode;
	};

	static disableDebugAndStrictMode() {
		if (this.eventsDebugger) {
			Target.prototype.dispatchEvent = this.eventsDebugger.initialDispatcher;
			Target.prototype.addEventListener = this.eventsDebugger.initialListener;
		}
		this.eventsDebugger = null;
	};

	static declareEventType(type: string, subType: string | null = null) {
		const eventType = {
			type,
			subType
		};
		if (Target.hasEventType.call(this, type, subType)) {
			throw E.user('Event type already exists!', {eventType});
		}
		this.eventsTypes.push(eventType);
	};

	static hasEventType(type: string, subType: string | null = null) {
		return (!!this.eventsTypes.find(x => (
			(x.type === type) &&
			((x.subType === subType) || (x.subType === '*') || (subType === '*'))
		)));
	};

	static initializeEventsTarget() {
		this.eventsTypes = [];
	};

	abstract getParent(): TBaseTarget | null;

	abstract getChilds(): TBaseTarget[];

	getAllChilds(): TBaseTarget[] {
		return (<TBaseTarget[]>Array.from(new Set(
			this.getChilds().
			map(x => [x, x.getAllChilds()]).
			flat(2)
		)));
	};

	abstract id: unknown;
	private readonly listeners: Map<string, Listener<TTarget, TBaseTarget>[]> = new Map; // listeners of others components listening this target events
	private readonly ownListeners: Set<Listener<TTarget, TBaseTarget>> = new Set; // listeners of this own component listening others components events
	isAncestorOf(target: TBaseTarget) {
		for (let parent = target.getParent(); parent; parent = parent.getParent()) {
			if (parent === <Target<any, any>>this) {
				return true;
			}
		}
		return false;
	};

	removeOwnListeners() {
		for (const listener of this.ownListeners) {
			listener.target.removeEventListener(<Listener<TBaseTarget, TBaseTarget>><any>listener);
		}
	};

	addEventListener(
		type: string,
		_listener: Listener<TTarget, TBaseTarget>
	): Listener<TTarget, TBaseTarget>;
	addEventListener(
		type: string,
		_listener: TListenerCallback<TTarget, TBaseTarget>,
		desc: Omit<IListenerDescriptor<TTarget, TBaseTarget>, 'callback' | 'target' | 'type'>
	): Listener<TTarget, TBaseTarget>;
	addEventListener(
		type: string,
		_listener: TListener<TTarget, TBaseTarget>,
		desc?: Omit<IListenerDescriptor<TTarget, TBaseTarget>, 'callback' | 'target' | 'type'>
	) {
		if (!(_listener instanceof Listener) && (!desc)) {
			throw E.user('Desc should be given if _listener is a method!', {
				type, _listener, desc
			});
		}
		const listener = (
			(typeof _listener === 'function') ? new Listener<TTarget, TBaseTarget>(Object.assign(
				{
					callback: _listener,
					target: <TTarget><Target<any, any>>this,
					type
				},
				desc
			)) : _listener
		);
		let listeners = this.listeners.get(type);
		if (!listeners) {
			listeners = [];
			this.listeners.set(type, listeners);
		}
		listener.listener.ownListeners.add(<Listener<TBaseTarget, TBaseTarget>><any>listener);
		//insert sorted
		let wasInserted = false;
		for (let i = 0; i < listeners.length; i++) {
			const currentListener = <Listener<TTarget, TBaseTarget>>listeners[i];
			if (currentListener.priority > listener.priority) {
				listeners.splice(i, 0, listener); // add before
				wasInserted = true;
				break;
			}
		}
		if (!wasInserted) {
			listeners.push(listener);
		}
		return (listener);
	};

	//Same as addEventListener but with placeholder for debugging / logging.
	//Specialized method to give key in hand debugging process with zero cost for the prod environment.
	//Do not use directly, use the debugger component which trigger debug mode for events when it's being enabled
	protected addEventListenerDebugAndStrictMode(
		type: string,
		_listener: TListener<TTarget, TBaseTarget>,
		desc?: Omit<IListenerDescriptor<TTarget, TBaseTarget>, 'callback' | 'target' | 'type'>
	) {
		if (!Target.eventsDebugger) {
			throw E.library('Unknown error', {});
		}
		if (!(_listener instanceof Listener) && (!desc)) {
			throw E.user('Desc should be given if _listener is a method!', {
				type, _listener, desc
			});
		}
		const listener = (
			(typeof _listener === 'function') ? new Listener<TTarget, TBaseTarget>(Object.assign(
				{
					callback: _listener,
					target: <TTarget><Target<any, any>>this,
					type
				},
				desc
			)) : _listener
		);
		if (
			!listener.disableEventExistsCheck &&
			!(<typeof Target<TTarget, TBaseTarget>>this.constructor).hasEventType(listener.type, listener.subType)
		) {
			const childsClasses = Array.from(new Set(
				(this.getAllChilds ? this.getAllChilds() : this.getChilds()).
				map(x => <typeof Target>x.constructor)
			));
			if (
				listener.isAtTarget ||
				!childsClasses.find(x => x.hasEventType(listener.type, listener.subType))
			) {
				throw E.user('Event type not exists!', {type, _listener, desc});
			}
		}
		const initialListener = <(
			type: string,
			_listener: Listener<any, any>
		) => Listener<any, any>>Target.eventsDebugger.initialListener;
		return initialListener.call(this, type, <Listener<any, any>>listener);
	};

	removeEventListener(
		listener: Listener<TTarget, TBaseTarget>
	) {
		const listeners = this.listeners.get(listener.type);
		if (!listeners) {
			throw E.user('Cannot remove not found listener!', {target: this});
		}
		const idx = listeners.indexOf(listener);
		if (idx === -1) {
			throw E.user('Cannot remove not found listener!', {target: this});
		}
		listeners.splice(idx, 1);
		listener.listener.ownListeners.delete(<Listener<TBaseTarget, TBaseTarget, any>><any>listener);
	};

	async dispatchEvent(
		//type,
		//descriptor
		...x: (
			[string] |
			[string, Omit<IEventDescriptor<TTarget, TBaseTarget>, 'target' | 'type'> | undefined]
			)
	): Promise<boolean>;
	async dispatchEvent(
		//event
		...x: [Event<TTarget, TBaseTarget>]
	): Promise<boolean>;
	async dispatchEvent(
		...x: [string] |
			[
				string,
					Omit<IEventDescriptor<TTarget, TBaseTarget>, 'target' | 'type'> | undefined
			] |
			[Event<TTarget, TBaseTarget>]
	): Promise<boolean> {
		let event: Event<TTarget, TBaseTarget> | undefined;
		try {
			event = this.dispatchEvent_eventFromParameters(...x);
			return (await this.dispatchEvent_internal(event));
		} catch (error: any) {
			throw E.bindAsBound('Error during event handling', {
				eventOrDispatchParameters: event || x
			}, error);
		}
	};

	//Same as dispatchEvent but with a placeholder for debugging / logging.
	//Specialized method to give a key in-hand debugging process with zero cost for the prod environment.
	//Do not use directly, use the debugger component that triggers debug mode for events when it's being enabled
	protected async dispatchEventDebugAndStrictMode(
		...x: [string] |
			[
				string,
					Omit<IEventDescriptor<TTarget, TBaseTarget>, 'target' | 'type'> | undefined
			] |
			[Event<TTarget, TBaseTarget>]
	) {
		if (!Target.eventsDebugger) {
			throw E.library('Unknown error', {});
		}
		/** Make a copy because some events can end the debugging and so, crash the following code. **/
		const ED = Target.eventsDebugger;
		let event: Event<TTarget, TBaseTarget> | undefined;
		try {
			event = this.dispatchEvent_eventFromParameters(...x);
			ED.onEventTrigger(event);
			if (!(<typeof Target<TTarget, TBaseTarget>>this.constructor).hasEventType(event.type, event.subType)) {
				throw E.user('Event type not exists on the target!', {
					event,
					Target: this.constructor
				});
			}
			const res = await this.dispatchEvent_internal(event);
			ED.onEventTriggered(event);
			return (res);
		} catch (error: any) {
			throw E.bindAsBound('Error during event handling', {
				event,
				target: this
			}, error);
		}
	};

	private dispatchEvent_eventFromParameters(
		...x: (
			[string] |
			[
				string,
					Omit<IEventDescriptor<TTarget, TBaseTarget>, 'target' | 'type'> | undefined
			] |
			[Event<TTarget, TBaseTarget>]
			)
	): Event<TTarget, TBaseTarget> {
		const x0 = x[0];
		return (
			(x0 instanceof Event) ? x0 :
				new Event(Object.assign(
					{},
					x[1],
					{
						target: <TTarget><Target<any, any>>this,
						type: x0
					}
				))
		);
	};


	private async dispatchEvent_internal_parentsUp(event: Event<TTarget, TBaseTarget>) {
		const bottomToTop = event.composedPath();
		event.phase = EventPhase.atTarget;
		for (const currentTarget of bottomToTop) {
			const listeners = currentTarget.listeners.get(event.type);
			if (listeners) {
				event.currentTarget = currentTarget;
				for (const listener of listeners) {
					if ((listener.subType === '*') || (listener.subType === event.subType)) {
						try {
							event.currentListener = listener.listener;
							await listener.handleEvent(event);
							event.currentListener = null;
						} catch (error: any) {
							throw E.bindAsBound('Error during event handling', {
								event,
								listener
							}, error);
						}
					}
				}
			}
		}
	};

	private async dispatchEvent_internal_parentsDown(event: Event<TTarget, TBaseTarget>) {
		const bottomToTop = event.composedPath();
		const topToBottom = Array.from(bottomToTop).reverse();
		event.phase = EventPhase.atTarget;
		for (const currentTarget of topToBottom) {
			const listeners = currentTarget.listeners.get(event.type);
			if (listeners) {
				event.currentTarget = currentTarget;
				for (const listener of listeners) {
					if ((listener.subType === '*') || (listener.subType === event.subType)) {
						try {
							event.currentListener = listener.listener;
							await listener.handleEvent(event);
							event.currentListener = null;
						} catch (error: any) {
							throw E.bindAsBound('Error during event handling', {
								event,
								listener
							}, error);
						}
					}
				}
			}
		}
	};

	private async dispatchEvent_internal_parentsDownUp(event: Event<TTarget, TBaseTarget>) {
		const type = event.type;
		const bottomToTop = event.composedPath().slice(1); // skip itself
		const topToBottom = Array.from(bottomToTop).reverse();
		event.phase = EventPhase.capturing;
		//capture
		for (const target of topToBottom) {
			if (event.isPropagationStopped) {
				break;
			}
			const listeners = target.listeners.get(type);
			if (!listeners) {
				continue;
			}
			event.currentTarget = target;
			for (const listener of listeners) {
				if (event.isPropagationImmediatelyStopped) {
					break;
				}
				if (
					listener.isCapture &&
					((listener.subType === '*') || (listener.subType === event.subType))
				) {
					try {
						event.currentListener = listener.listener;
						await listener.handleEvent(event);
						event.currentListener = null;
					} catch (error: any) {
						throw E.bindAsBound('Error during event handling', {
							event,
							listener
						}, error);
					}
				}
			}
		}
		if (event.isPropagationStopped) {
			return;
		}
		//at target
		const listeners = this.listeners.get(type);
		if (listeners) {
			event.phase = EventPhase.atTarget;
			event.currentTarget = <TTarget><Target<any, any>>this;
			for (const listener of listeners) {
				if ((listener.subType === '*') || (listener.subType === event.subType)) {
					try {
						event.currentListener = listener.listener;
						await listener.handleEvent(event);
						event.currentListener = null;
					} catch (error: any) {
						throw E.bindAsBound('Error during event handling', {
							event,
							listener
						}, error);
					}
				}
			}
		}
		if (event.isPropagationStopped) {
			return;
		}
		//bubble
		event.phase = EventPhase.bubbling;
		for (const target of bottomToTop) {
			const listeners = target.listeners.get(type);
			if (!listeners) {
				continue;
			}
			event.currentTarget = target;
			for (const listener of listeners) {
				if (event.isPropagationImmediatelyStopped) {
					break;
				}
				if (
					!listener.isCapture &&
					!listener.isAtTarget &&
					((listener.subType === '*') || (listener.subType === event.subType))
				) {
					try {
						event.currentListener = listener.listener;
						await listener.handleEvent(event);
						event.currentListener = null;
					} catch (error: any) {
						throw E.bindAsBound('Error during event handling', {
							event,
							listener
						}, error);
					}
				}
			}
		}
	};

// |	A
// |	B         | C | D         |
// |	E | F | G |---| H | I | J |
// |	K |-------+   +---+----+--+
// +---+
// DOWN <=> ABEKFGCDHIJ
// UP   <=> JIHDCGFKEBA
	private dispatchEvent_internal_childs_scan(): TBaseTarget[] {
		let depth = 1;
		const out: (TBaseTarget | TBaseTarget[])[] = [<TBaseTarget><any>this];
		const stack = [<[TBaseTarget, typeof out, number]>[
			<TBaseTarget><any>this, // Parent component
			out, // Children
			depth
		]];
		while (stack.length !== 0) {
			const [target, out, currentDepth] = <typeof stack[0]>stack.shift();
			if (currentDepth > depth) {
				depth = currentDepth;
			}
			for (const c of target.getChilds()) {
				const _out = [c];
				out.push(_out);
				stack.push([c, </* ts wtf*/any>_out, depth + 1]);
			}
		}
		return (<TTarget[]>out.flat(depth));
	};

	//no { capture; bubbling; atTarget } / handle all nodes with atTarget attribute
	private async dispatchEvent_internal_childsUp(event: Event<TTarget, TBaseTarget>) {
		const topToBottom = this.dispatchEvent_internal_childs_scan();
		const bottomToTop = topToBottom.reverse();
		event.phase = EventPhase.atTarget;
		for (const currentTarget of bottomToTop) {
			const listeners = currentTarget.listeners.get(event.type);
			if (listeners) {
				event.currentTarget = currentTarget;
				for (const listener of listeners) {
					if ((listener.subType === '*') || (listener.subType === event.subType)) {
						try {
							event.currentListener = listener.listener;
							await listener.handleEvent(event);
							event.currentListener = null;
						} catch (error: any) {
							throw E.bindAsBound('Error during event handling', {
								event,
								listener
							}, error);
						}
					}
				}
			}
		}
	};

	private async dispatchEvent_internal_childsDown(event: Event<TTarget, TBaseTarget>) {
		const topToBottom = this.dispatchEvent_internal_childs_scan();
		event.phase = EventPhase.atTarget;
		while (topToBottom.length !== 0) {
			const currentTarget = <TTarget>topToBottom.shift();
			const listeners = currentTarget.listeners.get(event.type);
			if (listeners) {
				event.currentTarget = currentTarget;
				for (const listener of listeners) {
					if ((listener.subType === '*') || (listener.subType === event.subType)) {
						try {
							event.currentListener = listener.listener;
							await listener.handleEvent(event);
							event.currentListener = null;
						} catch (error: any) {
							throw E.bindAsBound('Error during event handling', {
								event,
								listener
							}, error);
						}
					}
				}
			}
			if (event.isPropagationStopped) {
				if (event.isPropagationImmediatelyStopped) {
					break;
				}
				let i = 0;
				while (i < topToBottom.length) {
					const target = <TTarget>topToBottom[i];
					if (currentTarget.isAncestorOf(target)) {
						topToBottom.splice(i, 1);
					} else {
						i++;
					}
				}
				event.isPropagationStopped = false;
			}
		}
	};

	private async dispatchEvent_internal_childsDownUp(event: Event<TTarget, TBaseTarget>) {
		const topToBottom = this.dispatchEvent_internal_childs_scan().slice(1);
		const bottomToTop = topToBottom.reverse();
		event.phase = EventPhase.capturing;
		const type = event.type;
		//capture
		for (const target of topToBottom) {
			const listeners = target.listeners.get(type);
			if (!listeners) {
				continue;
			}
			event.currentTarget = target;
			for (const listener of listeners) {
				if (
					listener.isCapture &&
					((listener.subType === '*') || (listener.subType === event.subType))
				) {
					try {
						event.currentListener = listener.listener;
						await listener.handleEvent(event);
						event.currentListener = null;
					} catch (error: any) {
						throw E.bindAsBound('Error during event handling', {
							event,
							listener
						}, error);
					}
				}
			}
		}
		//at target
		const listeners = this.listeners.get(type);
		if (listeners) {
			event.phase = EventPhase.atTarget;
			event.currentTarget = <TTarget><any>this;
			for (const listener of listeners) {
				if ((listener.subType === '*') || (listener.subType === event.subType)) {
					try {
						event.currentListener = listener.listener;
						await listener.handleEvent(event);
						event.currentListener = null;
					} catch (error: any) {
						throw E.bindAsBound('Error during event handling', {
							event,
							listener
						}, error);
					}
				}
			}
		}
		//bubble
		event.phase = EventPhase.bubbling;
		for (const target of bottomToTop) {
			const listeners = target.listeners.get(type);
			if (!listeners) {
				continue;
			}
			event.currentTarget = target;
			for (const listener of listeners) {
				if (
					!listener.isCapture &&
					!listener.isAtTarget &&
					((listener.subType === '*') || (listener.subType === event.subType))
				) {
					try {
						event.currentListener = listener.listener;
						await listener.handleEvent(event);
						event.currentListener = null;
					} catch (error: any) {
						throw E.bindAsBound('Error during event handling', {
							event,
							listener
						}, error);
					}
				}
			}
		}
	};

	private async dispatchEvent_internal_self(event: Event<TTarget, TBaseTarget>) {
		event.phase = EventPhase.atTarget;
		const listeners = this.listeners.get(event.type);
		if (listeners) {
			event.currentTarget = <TBaseTarget><TTarget><Target<any, any>>this;
			for (const listener of listeners) {
				if ((listener.subType === '*') || (listener.subType === event.subType)) {
					try {
						event.currentListener = listener.listener;
						await listener.handleEvent(event);
						event.currentListener = null;
					} catch (error: any) {
						throw E.bindAsBound('Error during event handling', {
							event,
							listener
						}, error);
					}
				}
			}
		}
	};


	private async dispatchEvent_internal(event: Event<TTarget, TBaseTarget>) {
		if (event.phase !== EventPhase.none) {
			throw E.user(
				'Event already dispatched or is in the triggering process!',
				{event}
			);
		}
		if (event.bubblingType === 'parents') {
			if (event.bubblingDirection === 'up') {
				await this.dispatchEvent_internal_parentsUp(event);
			} else if (event.bubblingDirection === 'down') {
				await this.dispatchEvent_internal_parentsDown(event);
			} else if (event.bubblingDirection === 'down-up') {
				await this.dispatchEvent_internal_parentsDownUp(event);
			}
		} else if (event.bubblingType === 'childs') {
			if (event.bubblingDirection === 'up') {
				await this.dispatchEvent_internal_childsUp(event);
			} else if (event.bubblingDirection === 'down') {
				await this.dispatchEvent_internal_childsDown(event);
			} else if (event.bubblingDirection === 'down-up') {
				await this.dispatchEvent_internal_childsDownUp(event);
			}
		} else {
			await this.dispatchEvent_internal_self(event);
		}
		event.phase = EventPhase.dispatched;
		return (false);
	};

	constructor(desc: ITargetDescriptor) {
		void desc;
	};
};

export abstract class SimpleTarget<
	TTarget extends SimpleTarget<TTarget>
> extends Target<TTarget, Target<any, any>> {
	override getChilds() {
		return ([]);
	};

	override getParent() {
		return (null);
	};
};
